/**
* @name: new-import-modal
* @author: claude
* @date: 2025-01-24
* @description：新的导入模态框组件
* @update: 2025-01-24
*/
<template>
  <div v-if="dialogVisible">
    <a-modal
      v-model:visible="dialogVisible"
      :width="900"
      class="new-import-modal"
      :closable="true"
      :footer="null"
      :title="windowTitle"
      :destroyOnClose="true"
      :wrapStyle="{ overflow: 'hidden' }"
    >
      <div class="import-modal-content">
        <!-- 步骤指示器 -->
        <a-steps :current="currentStep" size="small" class="import-steps">
          <a-step title="上传文件" />
          <a-step title="预览确认" />
          <a-step title="提交导入" />
        </a-steps>

        <!-- 第一步：上传文件 -->
        <div v-show="currentStep === 0" class="step-content">
          <div class="upload-section">
            <a-upload-dragger
              :headers="uploadHeaders"
              :accept="accept"
              :data="uploadParams"
              :before-upload="beforeUpload"
              @change="handleFileSelect"
              :file-list="[]"
              :disabled="uploading"
              :class="{ 'uploading': uploading, 'has-file': selectedFile, 'has-error': uploadError }"
            >
              <!-- 空闲状态 -->
              <div v-if="!selectedFile && !uploading && !uploadError" class="upload-idle">
                <p class="ant-upload-drag-icon">
                  <inbox-outlined />
                </p>
                <p class="ant-upload-text">点击或拖拽文件到此处上传</p>
                <p class="ant-upload-hint">
                  支持 .xlsx .xls 格式，文件大小不超过 10MB
                </p>
              </div>

              <!-- 上传中状态（按钮loading，区域不显示进度） -->
              <div v-if="selectedFile && uploading" class="upload-uploading">
                <file-outlined class="upload-file-icon uploading-icon" />
                <p class="upload-text">{{ selectedFile.name }}</p>
                <div class="upload-info-line">
                  <span class="upload-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
              </div>

              <!-- 上传成功状态 -->
              <div v-if="selectedFile && !uploading && !uploadError" class="upload-success">
                <file-outlined class="upload-file-icon" />
                <p class="upload-text">{{ selectedFile.name }}</p>
                <div class="upload-info-line">
                  <span class="upload-size">{{ formatFileSize(selectedFile.size) }}</span>
                  <delete-outlined class="delete-icon" @click.stop="removeFile" />
                </div>
              </div>

              <!-- 上传失败状态 -->
              <div v-if="uploadError" class="upload-error">
                <p class="upload-text">{{ selectedFile?.name || '上传失败' }}</p>
                <div class="upload-info-line">
                  <span class="error-message">{{ uploadError }}</span>
                  <delete-outlined class="delete-icon" @click.stop="removeFile" />
                </div>
              </div>
            </a-upload-dragger>

            <!-- 模板下载 -->
            <div class="template-download" v-if="props.showTempDown !== false">
              <a-alert type="info" show-icon closable>
                <template #message>
                  为了保证数据导入顺利，推荐您下载使用
                  <a-button type="link" size="small" @click="downloadTemplate">
                    <template #icon><download-outlined /></template>
                    导入模版
                  </a-button>
                  ，并按照规范示例录入数据。
                </template>
              </a-alert>
            </div>

            <!-- Excel规范说明 -->
            <div class="excel-specs">
              <div class="spec-title">上传的 Excel 表符合以下规范：</div>
              <ul class="spec-list">
                <li>• 文件大小不超过10M，且单个sheet页数据量不超过5000行。当触发校验规则、关联业务规则和第三方服务回调时，单个sheet页数据量不超过 5000 行</li>
                <li>• 仅支持 (*.xls 和 *.xlsx)文件</li>
                <li>• 请确保您需要导入的sheet表头中不包含空的单元格，否则该sheet页数据系统将不做导入</li>
                <li>• 批量导入的数据不支持以"内置变量"作为条件的过滤</li>
                <li>• 导入文件不支持Excel公式计算，如SUM，=H2*J2等</li>
                <li>• 目前导入图片/附件会转存，占用一定附件容量</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 第二步：预览确认 -->
        <div v-show="currentStep === 1" class="step-content is-preview">
          <a-alert type="success" show-icon style="margin-bottom: 16px;">
            <template #message>
              解析完成，共 <strong>{{ recordCount }}</strong> 条数据
            </template>
          </a-alert>

          <div ref="previewTableWrapRef" class="preview-table-wrapper">
            <a-table
              :columns="previewColumns"
              :data-source="currentPageData"
              :pagination="false"
              :scroll="tableScroll"
              row-key="__previewKey"
              size="small"
              bordered
            />
          </div>
          <div v-if="recordCount > 0" class="preview-pagination">
            <a-pagination
              v-model:current="previewCurrentPage"
              v-model:pageSize="previewPageSize"
              :total="recordCount"
              :page-size-options="['10', '20', '50', '100']"
              :show-total="(total) => `共 ${total} 条`"
              size="small"
              :show-size-changer="true"
              @change="onPreviewPageChange"
            />
          </div>

          <!-- 导入选项 -->
          <div class="import-options">
            <!-- 导入模式 -->
            <div class="option-section import-mode-section">
              <div class="section-header">
                <div class="section-label">
                  <span class="label-icon"></span>
                  <span>导入模式</span>
                </div>
              </div>
              <div class="section-content">
                <a-radio-group v-model:value="importMode" class="inline-radio-group">
                  <a-radio value="1" class="inline-radio">新增</a-radio>
                  <a-radio v-if="props.supportUpdate" value="2" class="inline-radio">更新</a-radio>
                </a-radio-group>
              </div>
            </div>
            <!-- 错误处理 -->
            <div class="option-section error-handling-section">
              <div class="section-header">
                <div class="section-label">
                  <span class="label-icon"></span>
                  <span>错误处理</span>
                </div>
              </div>
              <div class="section-content">
                <a-radio-group v-model:value="errorHandling" class="inline-radio-group">
                  <a-radio value="1" class="inline-radio">任何错误即停止</a-radio>
                  <a-radio value="2" class="inline-radio">跳过错误数据，导入正确数据</a-radio>
                </a-radio-group>
                <!-- 提示信息 -->
                <div class="option-hint">
                  <info-circle-outlined />
                  <span>{{ errorHandlingHint }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 第三步：提交结果 -->
        <div v-show="currentStep === 2" class="step-content">
          <div class="result-content">
            <!-- 导入失败 -->
            <div v-if="importFailed" class="import-failed">
              <close-circle-outlined class="failed-icon" />
              <h3>导入失败</h3>
              <p class="failed-message">{{ importErrorMsg }}</p>
            </div>

            <!-- 同步导入：进度条 -->
            <div v-else-if="isSyncImport && importing" class="importing">
              <a-spin size="large" />
              <p style="margin-top: 16px;">正在导入...</p>
              <a-progress :percent="importProgress" :status="importProgressStatus" />
            </div>

            <!-- 异步导入：回包 data.async === true -->
            <div v-else-if="!importing && isAsyncSubmitted" class="sync-result">
              <check-circle-outlined class="success-icon" />
              <div class="result-text">
                <span style="color: #171a1d;font-size: 18px;font-weight: 500;">导入任务已提交，请到操作记录中查看导入结果</span>
              </div>
            </div>

            <!-- 同步导入完成 -->
            <div v-else-if="isSyncImport && !importing" class="sync-result">
              <check-circle-outlined class="success-icon" />
              <div class="result-text">
                <span style="color: #171a1d;font-size: 18px;font-weight: 500;">数据导入完成，
                  成功
                  <span class="count-success">{{ successCount }}</span>
                  <span>条！</span>
                  <span>失败</span>
                  <span class="count-fail">{{ failCount }}</span>
                  <span>条！</span>
                </span>
              </div>

              <!-- 错误信息展示 -->
              <div v-if="hasErrors && errData.length > 0" class="error-info-section">
                <div class="error-info-header">
                  <span class="error-info-title">错误信息：</span>
                </div>
                <div class="error-list-wrapper">
                  <a-list item-layout="horizontal" size="small" :data-source="errData">
                    <template #renderItem="{ item }">
                      <a-list-item>
                        <a-list-item-meta>
                          <template #title>
                            <div class="error-item-content">
                              <div class="error-item-left">
                                <span class="error-row-num">excel行号：{{ item.rowNum }}</span>
                                <span v-if="item.result === '导入失败'" class="error-msg">{{ item.msg }}</span>
                              </div>
                              <div class="error-item-right">
                                <a-tag v-if="item.result === '导入失败'" color="#F72229">{{ item.result }}</a-tag>
                                <a-tag v-else color="#87d068">{{ item.result }}</a-tag>
                              </div>
                            </div>
                          </template>
                        </a-list-item-meta>
                      </a-list-item>
                    </template>
                  </a-list>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="modal-footer">
          <a-button @click="handleCancel">{{ cancelButtonText }}</a-button>
          <a-button v-if="currentStep === 1" @click="currentStep--">上一步</a-button>
          <a-button
            v-if="currentStep === 0"
            type="primary"
            @click="handleNext"
            :disabled="!selectedFile"
            :loading="uploading"
          >
            下一步
          </a-button>
          <a-button
            v-if="currentStep === 1"
            type="primary"
            @click="handleSubmit"
            :loading="submitting"
          >
            开始导入
          </a-button>
          <a-button
            v-if="currentStep === 2 && isSyncImport && !importing && !importFailed && !isAsyncSubmitted"
            type="primary"
            @click="downloadResult"
          >
            下载结果详情
          </a-button>
        </div>
      </div>
    </a-modal>

  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

export default defineComponent({
  name: 'new-import-modal',
});
</script>

<script lang="ts" setup>
import {ref, computed, watch, unref, h, nextTick} from 'vue';
import {Modal as AModal, Steps as ASteps, Step as AStep,
  UploadDragger as AUploadDragger, Alert as AAlert, Button as AButton,
  Table as ATable, Pagination as APagination, RadioGroup as ARadioGroup,
  Radio as ARadio, Spin as ASpin, Progress as AProgress,
  List as AList, ListItem as AListItem, ListItemMeta as AListItemMeta, Tag as ATag, message} from 'ant-design-vue';
import {InboxOutlined, DownloadOutlined, InfoCircleOutlined,
  CheckCircleOutlined, FileOutlined, DeleteOutlined,
  CloseCircleOutlined, ReloadOutlined} from '@ant-design/icons-vue';
import {useVModel} from "@vueuse/core";
import {QueryCommand} from "xmgoing-server";
import {getParamsRelVal} from "@/main-page/utils/domainQueryUtils";
import {deepClone, StorageUtils} from "xmgoing-utils";
import GoingUtils from "@/utils/goingUtils";
import {usePageModelsStore} from "@/store/modules/pageModels";

const pageModelsStore = usePageModelsStore();

// 获取当前用户的token
const token = StorageUtils.getSessionItem("sessionId");
const uploadHeaders = ref({
  "Blade-Auth": `bearer ${token}`,
  Authorization: `Basic c2FiZXI6c2FiZXJfc2VjcmV0`,
  "Page-Code": GoingUtils.getPageCode(),
});

interface propsType {
  visible: boolean;
  windowTitle: string;
  props: any;
  params: any;
}

const propsDef = withDefaults(defineProps<propsType>(), {
  visible: false,
  windowTitle: '导入数据',
  props: {},
  params: {},
});

const {props, params} = toRefs(propsDef);
const emits = defineEmits(['update:visible', 'closeWin', 'refresh']);
const dialogVisible = useVModel(propsDef, 'visible', emits);

// 步骤状态
const currentStep = ref(0);
const uploading = ref(false);
const submitting = ref(false);
const importing = ref(false);
const selectedFile = ref<any>(null);
const recordCount = ref(0);
const excelCode = ref('');

// 上传进度相关
const uploadProgress = ref(0);
const uploadProgressStatus = ref<'normal' | 'exception' | 'success'>('normal');
const uploadError = ref('');

// 上传配置
const accept = ref('.xlsx,.xls');

// 解析接口（第一步调用）
const parseAction = computed(() => {
  return unref(props).parseUrl || unref(props).uploadUrl || `/api/go-lcc/execute/importByTableCode/${unref(props).tableCode}`;
});

// 导入接口（第二步调用）
const importAction = computed(() => {
  return unref(props).uploadUrl || `/api/go-lcc/execute/importByTableCode/${unref(props).tableCode}`;
});
const uploadParams = computed(() => {
  const result: any = {};
  // 添加导入参数
  const queryParamsDs: any = deepClone(unref(props).importParams || []);
  if (Array.isArray(queryParamsDs)) {
    queryParamsDs.forEach((itemDb) => {
      const {filterValue, paraName, filterSubValue, valueType} = itemDb;
      const relValue = getParamsRelVal({
        paramValType: valueType,
        paramVal: filterValue,
        varSubParam: filterSubValue,
        pageModelsStore,
        editorRecord: {},
        flowParams: []
      });
      result[paraName] = relValue;
    });
  }
  // 添加额外参数
  return {...result, ...unref(params)};
});

// 导入配置
const importMode = ref('1');
const errorHandling = ref('1');
const isSyncImport = ref(false);
const isAsyncSubmitted = ref(false);
const importProgress = ref(0);
const importProgressStatus = ref<'normal' | 'exception' | 'success'>('normal');

// 导入结果
const successCount = ref(0);
const failCount = ref(0);
const errData = ref<any[]>([]);
const hasErrors = ref(false);
const importFailed = ref(false);
const importErrorMsg = ref('');

// 预览数据
const previewColumns = ref<any[]>([]);
const previewData = ref<any[]>([]);
const previewPagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: false,
});

// 预览分页状态
const previewCurrentPage = ref(1);
const previewPageSize = ref(10);

const previewTableWrapRef = ref<HTMLElement | null>(null);

// 当前页数据（前端分页）
const currentPageData = computed(() => {
  const start = (previewCurrentPage.value - 1) * previewPageSize.value;
  const end = start + previewPageSize.value;
  return previewData.value.slice(start, end);
});

// 只做横向滚动。纵向用分页切换，避免表体把当前页裁掉。
const tableScroll = computed(() => {
  const columnsWidth = previewColumns.value.reduce((sum, col) => {
    const width = Number(col?.width);
    return sum + (Number.isFinite(width) && width > 0 ? width : 150);
  }, 0);
  return {
    x: Math.max(columnsWidth, 1),
  };
});

// 计算属性
const errorHandlingHint = computed(() => {
  if (errorHandling.value === '1') {
    return '任何错误即停止：遇到错误全部回滚，错误报告显示所有问题行';
  } else {
    return '跳过错误数据，导入正确数据：失败行跳过，成功行正常导入，错误报告显示失败原因';
  }
});

const cancelButtonText = computed(() => {
  return currentStep.value === 2 ? '关闭' : '取消';
});

// 监听visible变化
watch(dialogVisible, (newVal) => {
  if (!newVal) {
    resetModal();
  }
});

// 重置模态框
const resetModal = () => {
  uploading.value = false;
  submitting.value = false;
  currentStep.value = 0;
  selectedFile.value = null;
  recordCount.value = 0;
  importMode.value = '1';
  errorHandling.value = '1';
  isSyncImport.value = false;
  isAsyncSubmitted.value = false;
  importProgress.value = 0;
  successCount.value = 0;
  failCount.value = 0;
  errData.value = [];
  hasErrors.value = false;
  importFailed.value = false;
  importErrorMsg.value = '';
  previewColumns.value = [];
  previewData.value = [];
  excelCode.value = '';
  uploadProgress.value = 0;
  uploadProgressStatus.value = 'normal';
  uploadError.value = '';
  previewCurrentPage.value = 1;
  previewPageSize.value = 10;
};

// 文件上传前校验
const beforeUpload = (file: any) => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    message.error('文件大小不能超过10MB');
    return false;
  }
  const isValidType = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
  if (!isValidType) {
    message.error('只支持 .xlsx 和 .xls 格式的文件');
    return false;
  }
  // 返回 false 阻止自动上传
  return false;
};

// 文件选择 - 仅保存文件，不自动上传
const handleFileSelect = (info: any) => {
  const file = info.file;
  const fileObj = file.originFileObj || file;

  // 文件校验
  const maxSize = 10 * 1024 * 1024;
  if (fileObj.size > maxSize) {
    message.error('文件大小不能超过10MB');
    return;
  }
  const isValidType = fileObj.name.endsWith('.xlsx') || fileObj.name.endsWith('.xls');
  if (!isValidType) {
    message.error('只支持 .xlsx 和 .xls 格式的文件');
    return;
  }

  // 保存文件对象
  selectedFile.value = fileObj;

  // 重置上传状态
  uploadProgress.value = 0;
  uploadProgressStatus.value = 'normal';
  uploadError.value = '';
};

// 删除文件
const removeFile = () => {
  selectedFile.value = null;
  uploadProgress.value = 0;
  uploadProgressStatus.value = 'normal';
  uploadError.value = '';
};

// 重试上传
const retryUpload = () => {
  uploadError.value = '';
  uploadFile();
};

// 文件大小格式化
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
};

// 实际上传文件
const uploadFile = () => {
  return new Promise<boolean>((resolve) => {
    if (!selectedFile.value) {
      resolve(false);
      return;
    }

    uploading.value = true;
    uploadProgress.value = 0;
    uploadProgressStatus.value = 'normal';
    uploadError.value = '';

    const formData = new FormData();
    formData.append('file', selectedFile.value);

    // 添加额外的上传参数
    Object.keys(uploadParams.value).forEach(key => {
      formData.append(key, uploadParams.value[key]);
    });

    // 使用 XMLHttpRequest 以支持上传进度
    const xhr = new XMLHttpRequest();

    // 上传进度监听
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        uploadProgress.value = Math.round((e.loaded / e.total) * 100);
      }
    });

    // 上传完成监听
    xhr.addEventListener('load', () => {
      uploading.value = false;

      if (xhr.status === 200) {
        try {
          const result = JSON.parse(xhr.responseText);

          if (result.success || result.code === 200) {
            const data = result.data || result;

            if (data.excelData && Array.isArray(data.excelData)) {
              recordCount.value = data.excelData.length;
              previewCurrentPage.value = 1;
              previewData.value = data.excelData.map((row: any, index: number) => ({
                ...(row || {}),
                __previewKey: index,
              }));
              previewColumns.value = data.columns?.map((col: any) => {
                const title = col.columnCaption || col.columnName;
                const isRequired = title.startsWith('*');

                // 如果列名以*开头，使用自定义渲染
                if (isRequired) {
                  return {
                    title: () => h('span', [
                      h('span', { style: { color: '#ff4d4f', fontWeight: 'bold', fontSize: '14px', marginRight: '4px' } }, '*'),
                      h('span', title.slice(1))
                    ]),
                    dataIndex: col.columnName,
                    key: col.columnName,
                    width: 150,
                  };
                }

                return {
                  title: title,
                  dataIndex: col.columnName,
                  key: col.columnName,
                  width: 150,
                };
              }) || [];
              previewPagination.value.total = data.excelData.length;
              excelCode.value = data.excelCode || '';
              isSyncImport.value = true;
            }

            uploadProgress.value = 100;
            uploadProgressStatus.value = 'success';
            message.success('文件解析成功');
            resolve(true);
          } else {
            uploadProgressStatus.value = 'exception';
            uploadError.value = result?.message || result?.msg || '文件解析失败';
            message.error(uploadError.value);
            resolve(false);
          }
        } catch (e) {
          uploadProgressStatus.value = 'exception';
          uploadError.value = '文件解析失败';
          message.error('文件解析失败');
          resolve(false);
        }
      } else {
        uploadProgressStatus.value = 'exception';
        uploadError.value = '文件上传失败';
        message.error('文件上传失败');
        resolve(false);
      }
    });

    // 上传错误监听
    xhr.addEventListener('error', () => {
      uploading.value = false;
      uploadProgressStatus.value = 'exception';
      uploadError.value = '文件上传失败';
      message.error('文件上传失败');
      resolve(false);
    });

    // 设置请求头并发送
    xhr.open('POST', parseAction.value);
    Object.keys(uploadHeaders.value).forEach(key => {
      xhr.setRequestHeader(key, uploadHeaders.value[key]);
    });
    xhr.send(formData);
  });
};

// 下一步
const handleNext = async () => {
  if (!selectedFile.value) {
    message.warning('请先选择文件');
    return;
  }

  const success = await uploadFile();
  if (success) {
    currentStep.value = 1;
  }
};

// 提交导入
const handleSubmit = () => {
  submitting.value = true;

  const importParams = {
    taskId: excelCode.value,
    importMode: importMode.value,
    errorHandling: errorHandling.value,
    ...uploadParams.value,
  };

  // 使用 XMLHttpRequest 调用导入接口
  const xhr = new XMLHttpRequest();
  xhr.open('POST', importAction.value);

  // 设置请求头
  Object.keys(uploadHeaders.value).forEach(key => {
    xhr.setRequestHeader(key, uploadHeaders.value[key]);
  });
  xhr.setRequestHeader('Content-Type', 'application/json');

  // 导入完成监听
  xhr.addEventListener('load', () => {
    submitting.value = false;

    if (xhr.status === 200) {
      try {
        const result = JSON.parse(xhr.responseText);

        if (result.success || result.code === 200) {
          const data = result.data || result;

          // 处理错误数据
          if (data.rowData && Array.isArray(data.rowData) && data.rowData.length > 0) {
            errData.value = data.rowData.filter(item => item.result === '导入失败');
            hasErrors.value = errData.value && errData.value.length > 0;
          } else {
            errData.value = [];
            hasErrors.value = false;
          }

          currentStep.value = 2;
          importing.value = false;

          if (data.async === true) {
            isAsyncSubmitted.value = true;
          } else {
            isAsyncSubmitted.value = false;
            successCount.value = data.successCount || 0;
            failCount.value = data.failCount || 0;
            message.success('导入完成');
            emits('refresh');
          }
        } else {
          // 导入接口返回失败
          importFailed.value = true;
          importErrorMsg.value = result?.message || result?.msg || '导入失败';
          currentStep.value = 2;
          importing.value = false;
        }
      } catch (e) {
        importFailed.value = true;
        importErrorMsg.value = '导入失败';
        currentStep.value = 2;
        importing.value = false;
      }
    } else {
      importFailed.value = true;
      importErrorMsg.value = '导入失败';
      currentStep.value = 2;
      importing.value = false;
    }
  });

  // 导入错误监听
  xhr.addEventListener('error', () => {
    submitting.value = false;
    message.error('导入失败');
  });

  // 发送请求
  xhr.send(JSON.stringify(importParams));

  // 进入第三步显示进度，等回包后再按 async 决定结果
  if (isSyncImport.value) {
    currentStep.value = 2;
    importing.value = true;

    // 模拟进度（等待实际请求返回）
    let progress = 0;
    const interval = setInterval(() => {
      if (progress < 90) {
        progress += Math.floor(Math.random() * 10) + 5;
        if (progress > 90) progress = 90;
        importProgress.value = progress;
      }
    }, 200);

    // 保存interval以便清除
    (handleSubmit as any)._progressInterval = interval;
  } else {
    currentStep.value = 2;
  }
};

// 下载模板
const downloadTemplate = () => {
  const queryCommand = new QueryCommand({showTip: false});
  queryCommand.executeExport(
    unref(props).downUrl || `/api/go-lcc/execute/exportTemplateByTableCode/${unref(props).tableCode}`,
    (unref(props).fileName || '导入模版') + '.xlsx',
    {}
  );
};

// 下载结果（GET 请求）
const downloadResult = async () => {
  const url = `/api/xm-mes/mesImport/download/${excelCode.value}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        ...uploadHeaders.value,
      }
    });

    if (!response.ok) {
      throw new Error('下载失败');
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `导入结果_${new Date().getTime()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    message.error('下载失败');
  }
};

// 预览分页变化
const onPreviewPageChange = (page: number, pageSize: number) => {
  const sizeChanged = pageSize !== previewPageSize.value;
  previewPageSize.value = pageSize;
  const maxPage = Math.max(1, Math.ceil(recordCount.value / pageSize) || 1);
  previewCurrentPage.value = sizeChanged ? 1 : Math.min(page, maxPage);
  nextTick(() => {
    previewTableWrapRef.value?.scrollTo({top: 0});
  });
};

// 取消/关闭
const handleCancel = () => {
  if (currentStep.value === 2) {
    dialogVisible.value = false;
    emits('closeWin', {flush: true});
  } else {
    dialogVisible.value = false;
    emits('closeWin', {flush: false});
  }
};

defineExpose({
  resetModal,
});
</script>

<style lang="less">
.new-import-modal {
  .ant-modal-body {
    padding: 20px;
    padding-bottom: 0;
  }

  .import-modal-content {
    .import-steps {
      margin-bottom: 16px;
    }

    .step-content {
      max-height: 65vh;
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: auto;

      &.is-preview {
        overflow: hidden;

        > .ant-alert,
        > .preview-pagination,
        > .import-options {
          flex-shrink: 0;
        }
      }

      .upload-section {
        .ant-upload-drag {
          padding: 40px;
          height: auto;
          min-height: 160px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          transition: all 0.3s;
          border: 2px dashed #d9d9d9;

          &:hover {
            border-color: #40a9ff;
          }

          .ant-upload-drag-icon {
            margin-bottom: 8px;
          }

          .ant-upload-text {
            margin-bottom: 4px;
          }

          .ant-upload-hint {
            margin-bottom: 0;
          }

          // 上传中状态样式
          &.uploading {
            border-color: #40a9ff;
            background: #f0f9ff;
          }

          // 错误状态样式
          &.has-error {
            border-color: #ff4d4f;
            background: #fff1f0;
          }
        }

        // 空闲状态
        .upload-idle {
          text-align: center;

          .ant-upload-drag-icon {
            font-size: 48px;
            color: #40a9ff;
            margin-bottom: 16px;
          }
        }

        // 通用上传文本样式
        .upload-text {
          color: #333;
          font-size: 14px;
          font-weight: 500;
          margin: 0 0 12px 0;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        // 上传信息行（文件大小 + 删除按钮）
        .upload-info-line {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;

          .upload-size {
            color: #999;
            font-size: 12px;
          }

          .upload-progress-text {
            color: #40a9ff;
            font-size: 13px;
            font-weight: 500;
          }

          .delete-icon {
            font-size: 16px;
            color: #999;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
              color: #ff4d4f;
            }
          }

          .error-message {
            color: #ff4d4f;
            font-size: 12px;
          }
        }

        // 上传成功状态
        .upload-success {
          display: flex;
          flex-direction: column;
          align-items: center;

          .upload-file-icon {
            font-size: 48px;
            color: #40a9ff;
            margin-bottom: 12px;
          }
        }

        // 上传中状态
        .upload-uploading {
          display: flex;
          flex-direction: column;
          align-items: center;

          .upload-file-icon {
            font-size: 48px;
            color: #40a9ff;
            margin-bottom: 12px;

            &.uploading-icon {
              animation: pulse 1.5s ease-in-out infinite;
            }
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        // 上传失败状态
        .upload-error {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .selected-file-info {
          margin-top: 12px;

          .file-info-content {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .file-info-main {
              display: flex;
              align-items: center;
              gap: 8px;
              flex: 1;

              .file-icon {
                font-size: 16px;
                color: #52c41a;
              }

              .file-name {
                font-weight: 500;
                color: #333;
              }

              .file-size {
                color: #999;
                font-size: 12px;
                margin-left: 8px;
              }
            }
          }
        }

        .template-download {
          margin-top: 12px;
        }

        .excel-specs {
          margin-top: 12px;
          padding: 12px;
          background: #fafafa;
          border-radius: 4px;

          .spec-title {
            font-weight: 600;
            color: #444;
            margin-bottom: 8px;
            font-size: 13px;
          }

          .spec-list {
            margin: 0;
            padding-left: 20px;
            color: #777;
            font-size: 12px;
            line-height: 1.6;

            li {
              margin-bottom: 2px;
            }
          }
        }
      }

      .preview-table-wrapper {
        flex: 1 1 auto;
        min-height: 0;
        margin-bottom: 0;
        overflow: auto;
      }

      .preview-pagination {
        padding: 10px 12px;
        border-top: 1px solid #e5e6eb;
        display: flex;
        justify-content: flex-end;
        flex-shrink: 0;
      }

      .import-options {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .option-section {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e5e6eb;
          transition: all 0.2s ease;

          .section-header {
            flex-shrink: 0;
            width: 100px;
          }

          .section-label {
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            font-size: 14px;
            color: #1d2129;

            .label-icon {
              width: 4px;
              height: 16px;
              border-radius: 2px;
            }
          }

          .section-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
        }

        // 导入模式模块
        .import-mode-section {
          border-left: 3px solid #1890ff;

          .section-label .label-icon {
            background: #1890ff;
          }
        }

        // 错误处理模块
        .error-handling-section {
          align-items: flex-start;
          border-left: 4px solid #ff7a45 !important;

          .section-header {
            padding-top: 2px;
          }

          .section-label .label-icon {
            background: #ff7a45;
          }
        }

        // 内联单选组
        :deep(.inline-radio-group) {
          display: flex;
          flex-direction: row;
          gap: 24px;

          .inline-radio {
            margin: 0;
            padding: 6px 12px;
            border-radius: 6px;
            transition: all 0.2s ease;

            &:hover {
              background: #f5f5f5;
            }

            &.ant-radio-wrapper-checked {
              font-weight: 500;
            }
          }
        }

        // 提示信息
        .option-hint {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          padding: 8px 12px;
          background: #f7f8fa;
          border-radius: 6px;
          font-size: 12px;
          color: #86909c;
          line-height: 1.5;

          .anticon {
            margin-top: 1px;
            color: #ff7a45;
            flex-shrink: 0;
          }
        }
      }

      .result-content {
        text-align: center;
        padding: 30px 20px;

        .success-icon {
          font-size: 56px;
          color: #52c41a;
          margin-bottom: 12px;
        }

        .import-failed {
          .failed-icon {
            font-size: 56px;
            color: #ff4d4f;
            margin-bottom: 12px;
          }

          h3 {
            font-size: 18px;
            color: #333;
            margin-bottom: 12px;
          }

          .failed-message {
            font-size: 14px;
            color: #666;
            margin: 0;
          }
        }

        .result-text {
          margin: 24px 0;

          .count-success {
            color: #52c41a;
            font-weight: bold;
          }

          .count-fail {
            color: #ff4d4f;
            font-weight: bold;
          }
        }

        // 错误信息展示区域
        .error-info-section {
          margin-top: 24px;
          padding: 16px;
          border: 1px solid #eee;
          border-radius: 4px;
          text-align: left;

          .error-info-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e5e6eb;

            .error-info-title {
              font-weight: 500;
              font-size: 14px;
              color: #333;
            }
          }

          .error-list-wrapper {
            max-height: 200px;
            overflow-y: auto;

            :deep(.ant-list-item) {
              padding: 8px 0;
              border-bottom: 1px solid #f0f0f0;

              &:last-child {
                border-bottom: none;
              }
            }

            .error-item-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;

              .error-item-left {
                display: flex;
                align-items: center;
                gap: 12px;
                flex: 1;

                .error-row-num {
                  color: #666;
                  font-size: 13px;
                }

                .error-msg {
                  color: #F72229;
                  font-size: 12px;
                }
              }

              .error-item-right {
                flex-shrink: 0;
              }
            }
          }
        }
      }
    }

    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 16px;
      padding: 12px 0 20px 0;
      border-top: 1px solid #f0f0f0;
    }
  }
}
</style>
